import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../services/auth.service';

/**
 * Un 401 no es un problema de conexión.
 *
 * Es la distinción que faltaba y que hacía que una sesión vencida se viera
 * como un servidor caído: **el servidor contestó perfecto**, dijo «tu sesión
 * terminó». Sin nadie que lo tradujera, cada pantalla mostraba su propio error
 * de red y la persona salía a revisar la conexión o a avisar que el sistema
 * estaba abajo.
 *
 * Ante un 401 se hace lo único que puede arreglarlo solo: **se olvida el token
 * guardado y se reintenta una vez**. Si estaba vencido, el segundo intento
 * sale con uno nuevo y la persona no se entera de nada. Si vuelve a fallar, la
 * sesión terminó de verdad y hay que iniciarla otra vez.
 *
 * Se reintenta **una sola vez**: con un token que ya no se puede renovar, un
 * reintento en bucle serían dos llamadas por cada una y ningún progreso.
 */
@Injectable()
export class SesionInterceptor implements HttpInterceptor {
  private auth = inject(AuthService);
  private router = inject(Router);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: unknown) => {
        const es401 = error instanceof HttpErrorResponse && error.status === 401;

        /* **Una consulta de fondo no puede echar a nadie.**

           El semáforo de sistemas pregunta cada minuto, también con la
           pestaña quieta. Si el token venció mientras nadie tocaba nada, esa
           consulta se llevaba puesta la sesión: la persona volvía a la
           pantalla y estaba en el login sin haber hecho nada. Una consulta
           que nadie pidió no puede tener esa consecuencia. Falla en silencio
           y la próxima acción de verdad decide si la sesión sigue viva. */
        if (req.headers.has('X-Consulta-De-Fondo')) {
          return throwError(() => error);
        }

        /* El reintento se marca en la propia petición: así no hace falta
           llevar estado suelto y dos llamadas en paralelo no se pisan. */
        if (!es401 || req.headers.has('X-Reintento-Sesion')) {
          return throwError(() => error);
        }

        this.auth.olvidarToken();

        return this.auth.getToken().pipe(
          switchMap((token) => {
            if (!token) {
              this.aIniciarSesion();
              return throwError(() => error);
            }

            return next.handle(req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
                'X-Reintento-Sesion': '1'
              }
            }));
          }),
          catchError((segundo: unknown) => {
            if (segundo instanceof HttpErrorResponse && segundo.status === 401) {
              this.aIniciarSesion();
            }

            return throwError(() => segundo);
          })
        );
      })
    );
  }

  private aIniciarSesion(): void {
    // Ya estando ahí, no se navega de nuevo: sería un bucle de redirecciones
    if (this.router.url.startsWith('/login')) return;

    /* Se borra la cuenta antes de irse. Si queda, el login la ve, cree que
       hay sesión y devuelve al tablero: el tablero vuelve a recibir 401 y
       manda acá otra vez. Ese ida y vuelta es el que hacía parecer que la
       pantalla se recargaba sola. */
    this.auth.cerrarSesionLocal();

    this.router.navigate(['/login'], { queryParams: { vencida: 1 } });
  }
}
